<script setup lang="ts">
import { computed, toRef } from "vue";

import { cardActions } from "@/action/cardActions";
import ActionMenuDropdown from "@/components/ActionMenu/ActionMenuDropdown.vue";
import { boardKey } from "@/components/board/injectKeys";
import InfoMessage from "@/components/ui/InfoMessage/InfoMessage.vue";
import type { BoardCard } from "@/model/card";
import { injectStrict } from "@/utils/vue";

import ActionConfirmModal from "../ActionConfirmModal.vue";
import BulkActionModal from "../BulkActionModal.vue";
import BlockedGroup from "../internal/BlockedGroup.vue";
import BulkActionGroup from "../internal/BulkActionGroup.vue";
import { useBulkActionTracking } from "../useBulkActionTracking";
import { useBulkSubtitle, useUnplanEligibility } from "../useEligibility";

const props = defineProps<{ cards: BoardCard[] }>();
const emit = defineEmits<{ unplan: [cards: BoardCard[]] }>();

const board = injectStrict(boardKey);
const cards = toRef(props, "cards");
const { shouldRender, unplannable, notUnplannable } = useUnplanEligibility(cards, board);

const single = computed(() => cards.value.length === 1);
const totalCount = computed(() => unplannable.value.length + notUnplannable.value.length);
const unplannableCount = computed(() => unplannable.value.length);
const subtitle = useBulkSubtitle(
  totalCount,
  unplannableCount,
  /*$t*/ "action.bulkUnplan.subtitlePartial",
);

const { trackOpened, trackConfirmed } = useBulkActionTracking("unplan", cards, [
  ["not-unplannable", notUnplannable],
]);

function onUnplan() {
  trackConfirmed();
  emit("unplan", unplannable.value);
}
</script>

<template>
  <ActionMenuDropdown
    v-if="shouldRender"
    :action="cardActions.unplan"
    width="auto"
    max-height="auto"
    :disable-teleport="single"
    @open="trackOpened"
  >
    <ActionConfirmModal
      v-if="single"
      :title="$t('action.unplanSticky.title')"
      :description="$t('action.unplanSticky.description')"
      :primary-label="$t('cardAction.unplan')"
      primary-icon="general/return"
      @primary="onUnplan"
    />
    <BulkActionModal
      v-else
      :title="$t('action.bulkUnplan.title')"
      :subtitle="subtitle"
      :primary-label="$t('action.bulkUnplan.primary', { count: unplannable.length })"
      primary-icon="general/return"
      :primary-disabled="unplannable.length === 0"
      @primary="onUnplan"
    >
      <BulkActionGroup
        :heading="$t('action.bulkUnplan.willBeUnplannedHeading')"
        :cards="unplannable"
      >
        <InfoMessage size="small" level="default">
          {{ $t("action.bulkUnplan.willBeUnplannedBanner", { count: unplannable.length }) }}
        </InfoMessage>
      </BulkActionGroup>

      <template v-if="notUnplannable.length > 0" #blocked>
        <BlockedGroup
          :title="$t('action.bulkUnplan.blockedNotUnplannable.title')"
          :description="$t('action.bulkUnplan.blockedNotUnplannable.description')"
          :cards="notUnplannable"
        />
      </template>
    </BulkActionModal>
  </ActionMenuDropdown>
</template>
